/**
 * NymoArt Voting - JavaScript v2.0.0
 * 
 * Random image voting from Firebase collections
 * 
 * @requires NymoArt (nymo-base.js)
 */

if (!customElements.get('nymo-voting')) {
  customElements.define('nymo-voting', class NymoVoting extends HTMLElement {
    
    constructor() {
      super();
      
      this.moduleId = this.dataset.moduleId || NymoArt.utils.uniqueId('voting-');
      
      // Configuration from data attributes
      this.config = {
        collections: (this.dataset.collections || '').split(',').map(c => c.trim()).filter(Boolean),
        batchSize: parseInt(this.dataset.batchSize, 10) || 5,
        preloadThreshold: parseInt(this.dataset.preloadThreshold, 10) || 3
      };
      
      // State
      this.state = {
        images: [],
        currentIndex: 0,
        isLoading: false,
        hasMoreImages: true,
        seenDesignIds: new Set(),
        collectionPageTokens: {}
      };
      
      // Element references
      this.elements = {};
      
      // Bound methods
      this._onVote = this._handleVote.bind(this);
      this._onKeydown = this._handleKeydown.bind(this);
    }
    
    connectedCallback() {
      // Get element references
      this.elements = {
        imageWrapper: this.querySelector('[data-image-wrapper]'),
        image: this.querySelector('[data-voting-image]'),
        loading: this.querySelector('[data-loading]'),
        title: this.querySelector('[data-title]'),
        subtitle: this.querySelector('[data-subtitle]'),
        stats: this.querySelector('[data-stats]'),
        voteButtons: this.querySelectorAll('[data-vote]'),
        complete: this.querySelector('[data-complete]')
      };
      
      // Bind events
      this._bindEvents();
      
      // Register module
      NymoArt.modules.register(`voting-${this.moduleId}`, this);
      
      // Initialize collections from config
      if (this.config.collections.length === 0) {
        this.config.collections = ['250501_new_ideas', 'Friends'];
      }
      
      // Start loading
      this.loadImages(true);
      
      NymoArt.log.info(`Voting initialized: ${this.moduleId}`, this.config);
    }
    
    disconnectedCallback() {
      this._unbindEvents();
      NymoArt.modules.unregister(`voting-${this.moduleId}`);
    }
    
    _bindEvents() {
      this.elements.voteButtons.forEach(btn => {
        btn.addEventListener('click', this._onVote);
      });
      
      document.addEventListener('keydown', this._onKeydown);
    }
    
    _unbindEvents() {
      this.elements.voteButtons.forEach(btn => {
        btn.removeEventListener('click', this._onVote);
      });
      
      document.removeEventListener('keydown', this._onKeydown);
    }
    
    _handleVote(e) {
      const voteType = e.currentTarget.dataset.vote;
      this.submitVote(voteType);
    }
    
    _handleKeydown(e) {
      // Only handle if focus is within this component or body
      if (!this.contains(document.activeElement) && document.activeElement !== document.body) return;
      
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        this.submitVote('like');
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this.submitVote('dislike');
      }
    }
    
    // ===== IMAGE LOADING =====
    
    async loadImages(isFirstLoad = true) {
      if (this.state.isLoading) return;
      
      try {
        this.state.isLoading = true;
        
        if (isFirstLoad) {
          this._showLoading('Lade Bilder...');
          this.config.collections.forEach(collection => {
            this.state.collectionPageTokens[collection] = null;
          });
        }
        
        NymoArt.log.info(`Loading images from: ${this.config.collections.join(', ')}`);
        
        // Load from all collections in parallel
        const results = await Promise.all(
          this.config.collections.map(c => this._loadFromCollection(c, isFirstLoad))
        );
        
        // Combine results
        let allNewImages = [];
        let anyHasMore = false;
        
        results.forEach(result => {
          allNewImages.push(...result.images);
          if (result.hasMore) anyHasMore = true;
        });
        
        this.state.hasMoreImages = anyHasMore;
        
        if (allNewImages.length === 0 && isFirstLoad) {
          this._showLoading('Keine Bilder gefunden');
          return;
        }
        
        // Add to images array
        this.state.images.push(...allNewImages);
        
        if (isFirstLoad) {
          this._shuffleArray(this.state.images);
          this._loadCurrentImage();
        } else {
          // Filter out seen images and reshuffle
          const unseenImages = this.state.images.filter(
            img => !this.state.seenDesignIds.has(img.designId)
          );
          this.state.images = unseenImages;
          this._shuffleArray(this.state.images);
          this.state.currentIndex = 0;
        }
        
      } catch (error) {
        NymoArt.log.error('Image loading failed:', error);
        this._showLoading(`Fehler: ${error.message}`);
      } finally {
        this.state.isLoading = false;
      }
    }
    
    async _loadFromCollection(collectionId, isFirstLoad) {
      try {
        // Get design IDs from collection subcollection
        let url = `${NymoArt.config.firebase.baseUrl}/collection/${collectionId}/images?pageSize=${this.config.batchSize}`;
        
        const pageToken = this.state.collectionPageTokens[collectionId];
        if (pageToken && !isFirstLoad) {
          url += `&pageToken=${pageToken}`;
        }
        
        const response = await fetch(url);
        if (!response.ok) {
          NymoArt.log.warn(`Collection ${collectionId} not found`);
          return { images: [], hasMore: false };
        }
        
        const data = await response.json();
        this.state.collectionPageTokens[collectionId] = data.nextPageToken || null;
        
        if (!data.documents?.length) {
          return { images: [], hasMore: false };
        }
        
        // Get full image data for each design
        const designIds = data.documents.map(doc => doc.name.split('/').pop());
        
        const imagePromises = designIds.map(async (designId) => {
          try {
            const imageData = await NymoArt.firebase.getDoc('image', designId);
            return {
              designId,
              collectionId,
              ...imageData
            };
          } catch (e) {
            return null;
          }
        });
        
        const images = (await Promise.all(imagePromises)).filter(img => img?.imageUrl);
        
        return {
          images,
          hasMore: !!data.nextPageToken
        };
        
      } catch (error) {
        NymoArt.log.error(`Error loading collection ${collectionId}:`, error);
        return { images: [], hasMore: false };
      }
    }
    
    _shuffleArray(array) {
      for (let i = array.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [array[i], array[j]] = [array[j], array[i]];
      }
    }
    
    _checkPreload() {
      const remaining = this.state.images.length - this.state.currentIndex - 1;
      
      if (remaining <= this.config.preloadThreshold && 
          this.state.hasMoreImages && 
          !this.state.isLoading) {
        NymoArt.log.info(`Preloading: ${remaining} remaining`);
        this.loadImages(false);
      }
    }
    
    _preloadNextImages() {
      // Preload next 3 images
      for (let i = 1; i <= 3; i++) {
        const nextIndex = (this.state.currentIndex + i) % this.state.images.length;
        const nextImage = this.state.images[nextIndex];
        if (nextImage && !this.state.seenDesignIds.has(nextImage.designId)) {
          const img = new Image();
          img.src = nextImage.imageUrl;
        }
      }
    }
    
    // ===== CURRENT IMAGE =====
    
    _loadCurrentImage() {
      // Find next unseen image
      let attempts = 0;
      while (
        this.state.seenDesignIds.has(this.state.images[this.state.currentIndex]?.designId) && 
        attempts < this.state.images.length
      ) {
        this.state.currentIndex = (this.state.currentIndex + 1) % this.state.images.length;
        attempts++;
      }
      
      // Check if all images seen
      if (attempts >= this.state.images.length) {
        if (this.state.hasMoreImages && !this.state.isLoading) {
          this.loadImages(false);
          return;
        } else {
          this._showComplete();
          return;
        }
      }
      
      const image = this.state.images[this.state.currentIndex];
      if (!image) return;
      
      // Show loading
      this.elements.image.dataset.loaded = 'false';
      this._showLoading('Lade Bild...');
      
      // Load image
      this.elements.image.onload = () => {
        this.elements.image.dataset.loaded = 'true';
        this._hideLoading();
        this._preloadNextImages();
      };
      
      this.elements.image.onerror = () => {
        this._showLoading('Bild konnte nicht geladen werden');
      };
      
      this.elements.image.src = image.imageUrl;
      
      // Update info
      this.elements.title.textContent = image.artist ? `Artist: ${image.artist}` : 'Unknown Artist';
      this.elements.subtitle.textContent = `${image.collectionId} #${image.designId}`;
      
      // Load vote stats
      this._loadVoteStats(image.designId);
      
      // Check if we need to preload more
      this._checkPreload();
    }
    
    async _loadVoteStats(designId) {
      try {
        const voteData = await NymoArt.firebase.getDoc('votes', designId);
        const likes = voteData.likes || 0;
        const dislikes = voteData.dislikes || 0;
        const total = likes + dislikes;
        
        this.elements.stats.textContent = total > 0
          ? `✓ ${likes}   (${total} Votes)   ✗ ${dislikes}`
          : 'Noch keine Votes';
      } catch (e) {
        this.elements.stats.textContent = 'Noch keine Votes';
      }
    }
    
    // ===== VOTING =====
    
    async submitVote(voteType) {
      if (this.state.images.length === 0) return;
      
      const image = this.state.images[this.state.currentIndex];
      if (!image) return;
      
      const { designId, collectionId, layoutVariant } = image;
      
      // Mark as seen
      this.state.seenDesignIds.add(designId);
      NymoArt.log.info(`Vote: ${voteType} for ${designId}`);
      
      // Disable buttons temporarily
      this.elements.voteButtons.forEach(btn => btn.disabled = true);
      
      try {
        // Get current votes
        let currentLikes = 0;
        let currentDislikes = 0;
        
        try {
          const voteData = await NymoArt.firebase.getDoc('votes', designId);
          currentLikes = voteData.likes || 0;
          currentDislikes = voteData.dislikes || 0;
        } catch (e) {
          // No votes yet
        }
        
        const newLikes = voteType === 'like' ? currentLikes + 1 : currentLikes;
        const newDislikes = voteType === 'dislike' ? currentDislikes + 1 : currentDislikes;
        
        // Update votes
        await NymoArt.firebase.updateDoc('votes', designId, {
          likes: newLikes,
          dislikes: newDislikes,
          designId,
          collectionId: collectionId || '',
          layoutVariant: layoutVariant || '',
          lastVoted: new Date(),
          lastUserAgent: navigator.userAgent
        });
        
        // Emit event
        NymoArt.events.emit('nymo:vote:submitted', {
          designId,
          voteType,
          newLikes,
          newDislikes
        });
        
      } catch (error) {
        NymoArt.log.error('Vote failed:', error);
      }
      
      // Move to next image
      this.state.currentIndex = (this.state.currentIndex + 1) % this.state.images.length;
      
      // Re-enable buttons and load next
      setTimeout(() => {
        this.elements.voteButtons.forEach(btn => btn.disabled = false);
        this._loadCurrentImage();
      }, 300);
    }
    
    // ===== UI HELPERS =====
    
    _showLoading(text) {
      this.elements.loading.textContent = text;
      this.elements.loading.hidden = false;
    }
    
    _hideLoading() {
      this.elements.loading.hidden = true;
    }
    
    _showComplete() {
      if (this.elements.complete) {
        this.elements.complete.hidden = false;
        this.elements.imageWrapper.hidden = true;
      } else {
        this._showLoading(`Alle ${this.state.seenDesignIds.size} Bilder bewertet! 🎉`);
      }
      
      this.elements.voteButtons.forEach(btn => btn.hidden = true);
    }
  });
  
  console.log('🗳️ NymoArt Voting v2.0.0 loaded');
}

